import React, { ReactNode } from 'react'
import { H6 } from '@blueprintjs/core'
import { Flex, IconButton, Button } from '@ui'
import { useTransition, animated, config } from 'react-spring'
import Spinner from '@ui/Spinner'
import css from './multiSelect.module.scss'

interface MultiSelectProps {
  title?: string
  values: ReactNode
  select: ReactNode
  onRemove?: (itemId: any) => void
  onClearAll?: () => void
  loading?: boolean
}

export default function MultiSelect({
  title,
  values,
  select,
  onRemove,
  onClearAll,
  loading,
}: MultiSelectProps) {
  const transitions = useTransition(values, (item: any) => item.key, {
    from: { transform: 'scale(0.8)', opacity: 0, height: 0 },
    enter: { transform: 'scale(1)', opacity: 1, height: 40 },
    leave: { transform: 'scale(0.8)', opacity: 0, height: 0 },
    config: { ...config.stiff, clamp: true },
  })

  return (
    <div>
      <Flex align="baseline">
        {!!title && <H6>{title}</H6>}
        {!!title && onClearAll && transitions.length > 1 && (
          <Flex align="center" style={{ marginLeft: 5 }}>
            <Button
              minimal
              small
              onClick={onClearAll}
              text="Clear all"
              style={{ color: 'var(--grey1)' }}
            />
          </Flex>
        )}
      </Flex>
      <Flex wrap="wrap">
        {transitions.map(({ item, props, key }, index) => {
          return (
            <animated.div
              key={key}
              style={{
                ...props,
                marginRight: 8,
                // setting height to undefined prevents larger items from
                // overlapping in unexpected ways. Let transition height = 40px
                // and this overwrite is a bit hacky but is safe overall
                height: undefined,
                paddingBottom: 10,
              }}
            >
              <FilterTag
                onRemove={
                  onRemove
                    ? () => {
                        /* eslint-disable @typescript-eslint/ban-ts-comment */
                        // @ts-ignore
                        onRemove(item.key)
                      }
                    : undefined
                }
              >
                {item}
              </FilterTag>
            </animated.div>
          )
        })}
        {!title && onClearAll && transitions.length > 1 && (
          // wrapped in flex to not stretch the button
          // padding bottom 8 to center align with tags
          <Flex align="center" style={{ paddingBottom: 8 }}>
            <Button minimal small onClick={onClearAll} text="Clear all" />
          </Flex>
        )}
        {loading && (
          <Flex align="center" style={{ paddingBottom: 8 }}>
            <Spinner size={20} />
          </Flex>
        )}
      </Flex>
      {select}
    </div>
  )
}

interface FilterTagProps {
  children: React.ReactNode
  onRemove?: () => void
}

export function FilterTag({ children, onRemove }: FilterTagProps) {
  return (
    <div className={css.filter_tag} style={{ paddingRight: onRemove ? undefined : 14 }}>
      {children}
      {onRemove && (
        <span style={{ marginLeft: 4 }}>
          <IconButton minimal iconName="times" onClick={onRemove} />
        </span>
      )}
    </div>
  )
}
